// ============================================================================
// Boot: decides what to show before the app itself loads any data.
//
//   - If the host already provides window.claude (the test harness, or the
//     original claude.ai Artifact runtime), render the app directly against it.
//   - Otherwise this is the self-hosted module: ask the server who's signed in,
//     show first-run setup / sign-in as needed, then install the server-backed
//     capabilities (src/platform/api.js) and render the app for that person.
// ============================================================================
import { App } from "./app/App.jsx";
import { LiveModeScreen } from "./app/screens/Live.jsx";
import { setCaps } from "./app/data.jsx";
import { LangContext, useLang, useT } from "./app/i18n.jsx";
import { LoginScreen, SetupScreen, authErrorText } from "./app/team/AuthScreens.jsx";
import { applyTextScale, readTextScale } from "./app/team/AccountMenu.jsx";
import { Api, installCapabilities, resetClientState } from "./platform/api.js";

const { useEffect, useState, useCallback } = React;

// Components the test suite renders on their own (outside the full App
// shell). Everything else stays private to the bundle.
window.RundownApp = { App, LiveModeScreen, setCaps };

// decided once, before sign-in installs our own window.claude
const HOST_PROVIDES_CAPABILITIES = !!window.claude;

function LoadingShell() {
  const { t } = useT();
  return <div role="status" style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#DBD9D0", fontFamily: "Inter, system-ui, sans-serif", color: "#6E6C63" }}>{t("connecting")}</div>;
}

function SelfHosted() {
  const { t } = useT();
  const [state, setState] = useState({ phase: "loading" });

  const refresh = useCallback(async (message) => {
    try {
      const [config, me] = await Promise.all([Api.config(), Api.me()]);
      if (config.setupRequired) return setState({ phase: "setup", config });
      if (!me) return setState({ phase: "login", config, message });
      installCapabilities(me);
      setState({ phase: "app", config, session: me });
    } catch (e) {
      setState({ phase: "error", message: e.message });
    }
  }, []);

  useEffect(() => {
    // an error code handed back by the Google sign-in redirect
    const params = new URLSearchParams(location.search);
    const authError = params.get("auth_error");
    if (authError) history.replaceState(null, "", location.pathname);
    refresh(authError ? authErrorText(authError, t) : "");
    const onSignedOut = () => { resetClientState(); refresh(t("sessionExpired")); };
    window.addEventListener("rundown:signed-out", onSignedOut);
    return () => window.removeEventListener("rundown:signed-out", onSignedOut);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const signOut = async () => {
    try { await Api.logout(); } catch (e) {}
    // the App unmounts (dropping its in-memory data) and the API layer forgets
    // this person's document versions before the sign-in screen shows
    resetClientState();
    setState((s) => ({ phase: "login", config: s.config, message: "" }));
  };

  if (state.phase === "loading") return <LoadingShell />;
  if (state.phase === "error") return <div role="alert" style={{ padding: 24, fontFamily: "system-ui" }}>{state.message}</div>;
  if (state.phase === "setup") return <SetupScreen config={state.config} onSignedIn={() => refresh()} />;
  if (state.phase === "login") return <LoginScreen config={state.config} initialError={state.message} onSignedIn={() => refresh()} />;
  return <App key={state.session.user.id} session={state.session} authConfig={state.config} onSignOut={signOut} />;
}

function Root() {
  const langCtx = useLang();
  useEffect(() => {
    document.documentElement.lang = langCtx.lang;
  }, [langCtx.lang]);
  return (
    <LangContext.Provider value={langCtx}>
      {HOST_PROVIDES_CAPABILITIES ? <App /> : <SelfHosted />}
    </LangContext.Provider>
  );
}

applyTextScale(readTextScale());
const rbRootEl = document.getElementById("root");
ReactDOM.createRoot(rbRootEl).render(<Root />);
